import React from "react";
import { Button } from "@/components/ui/button";
import { Phone, CalendarClock } from "lucide-react";
import { useConsultationModal } from "@/hooks/use-consultation-modal";

export type InlineCtaVariant = "top" | "mid" | "end";

interface ArticleInlineCtaProps {
  variant?: InlineCtaVariant;
  heading?: string;
  text?: string;
}

/**
 * Strategic in-article call-to-action. Opens a popup consultation form
 * (via useConsultationModal) so readers can submit their info without
 * leaving the article. Placed at the top, middle, and end of every article.
 */
export const ArticleInlineCta: React.FC<ArticleInlineCtaProps> = ({
  variant = "mid",
  heading,
  text,
}) => {
  const { openConsultation } = useConsultationModal();

  const defaults: Record<InlineCtaVariant, { heading: string; text: string }> =
    {
      top: {
        heading: "Want to know if SAINT® is right for you?",
        text: "Tell us about your treatment history and symptoms. A consultation is an evaluation, not a commitment — and it starts with your story.",
      },
      mid: {
        heading: "Curious whether you'd qualify?",
        text: "If you've tried medication and you're still not yourself, you may be a candidate for SAINT®. Our team will give you a straight answer.",
      },
      end: {
        heading: "Ready to talk to someone?",
        text: "The hardest part is usually the first phone call. Everything after that is a series of small, defined steps.",
      },
    };

  const content = {
    heading: heading || defaults[variant].heading,
    text: text || defaults[variant].text,
  };

  return (
    <section
      className={`py-10 sm:py-12 ${
        variant === "mid" ? "bg-light-blue/30" : "bg-background"
      }`}
      aria-label="Request a consultation"
    >
      <div className="container-px mx-auto max-w-3xl px-4">
        <div className="rounded-2xl border border-accent/30 bg-card p-6 sm:p-8 shadow-soft text-center space-y-4">
          <h3 className="font-headings text-xl sm:text-2xl font-extrabold text-primary leading-tight">
            {content.heading}
          </h3>
          <p className="text-foreground/80 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            {content.text}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button
              onClick={() => openConsultation(`article-inline-cta-${variant}`)}
              size="lg"
              className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
            >
              <CalendarClock className="h-5 w-5 mr-2" />
              Request a Consultation
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50"
            >
              <a href="tel:9548279793">
                <Phone className="h-4 w-4 mr-2" />
                954-827-9793
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticleInlineCta;
